import DataTable from "../../components/common/DataTable";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Paper, Loader, Text, Title, SimpleGrid } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";
import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Head from "../../components/common/Head";
import { getMethod } from "../../utils/api";

const CreatorDashboard = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateRange, setDateRange] = React.useState(() => {
    const startDateParam = searchParams.get("startDate");
    const endDateParam = searchParams.get("endDate");
    if (startDateParam && endDateParam) {
      return [dayjs(startDateParam), dayjs(endDateParam)];
    }
    return [dayjs().subtract(30, "days"), dayjs()];
  });

  const startDate =
    searchParams.get("startDate") || dateRange[0].format("YYYY-MM-DD");
  const endDate =
    searchParams.get("endDate") || dateRange[1].format("YYYY-MM-DD");

  const {
    data: dashboardData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["creatorDashboard", id, startDate, endDate],
    queryFn: () =>
      getMethod(
        `/admin/creator-dashboard/${id}?startDate=${startDate}&endDate=${endDate}`
      ).then((res) => res.data),
  });

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      const dStart = dayjs(dates[0]);
      const dEnd = dayjs(dates[1]);
      setDateRange([dStart, dEnd]);
      setSearchParams({ startDate: dStart.format("YYYY-MM-DD"), endDate: dEnd.format("YYYY-MM-DD") });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader size="lg" />
      </div>
    );
  }

  if (error) {
    return <Text c="red" p="xl">Error loading dashboard data</Text>;
  }

  if (!dashboardData) {
    return <Text c="dimmed" p="xl">No data available</Text>;
  }

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0";
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num;
  };

  const formatWatchTime = (seconds) => {
    if (seconds === null || seconds === undefined) return "0h";
    const hours = seconds / 3600;
    if (hours >= 1) return hours.toFixed(1) + "h";
    return (seconds / 60).toFixed(1) + "m";
  };

  const formatPercentage = (num) => {
    if (num === null || num === undefined) return "0%";
    return Math.abs(num).toFixed(2) + "%";
  };

  const calculatePercentageChange = (current, previous) => {
    if (!previous) return 100;
    return ((current - previous) / previous) * 100;
  };

  const statsData = [
    {
      title: "Total Views",
      value: formatNumber(dashboardData.thisMonthViews),
      change: {
        value: Math.abs(calculatePercentageChange(dashboardData.thisMonthViews, dashboardData.lastMonthViews))?.toFixed(2),
        type: dashboardData.thisMonthViews >= dashboardData.lastMonthViews ? "increase" : "decrease",
      },
      subtitle: `${dashboardData.thisMonthViews >= dashboardData.lastMonthViews ? "+" : ""}${formatPercentage(calculatePercentageChange(dashboardData.thisMonthViews, dashboardData.lastMonthViews))} from last month`,
    },
    {
      title: "Watch Time",
      value: formatWatchTime(dashboardData.thisMonthWatchtime),
      change: {
        value: Math.abs(calculatePercentageChange(dashboardData.thisMonthWatchtime, dashboardData.lastMonthWatchtime))?.toFixed(2),
        type: dashboardData.thisMonthWatchtime >= dashboardData.lastMonthWatchtime ? "increase" : "decrease",
      },
      subtitle: `${dashboardData.thisMonthWatchtime >= dashboardData.lastMonthWatchtime ? "+" : ""}${formatPercentage(calculatePercentageChange(dashboardData.thisMonthWatchtime, dashboardData.lastMonthWatchtime))} from last month`,
    },
    {
      title: "Engagement",
      value: `${dashboardData.thisMonthEngagement || 0}%`,
      change: {
        value: Math.abs(calculatePercentageChange(dashboardData.thisMonthEngagement, dashboardData.lastMonthEngagement))?.toFixed(2),
        type: dashboardData.thisMonthEngagement >= dashboardData.lastMonthEngagement ? "increase" : "decrease",
      },
      subtitle: `${dashboardData.thisMonthEngagement >= dashboardData.lastMonthEngagement ? "+" : ""}${formatPercentage(calculatePercentageChange(dashboardData.thisMonthEngagement, dashboardData.lastMonthEngagement))} from last month`,
    },
    {
      title: "Earnings",
      value: `Tsh ${dashboardData.thisMonthEarnings?.toFixed(2) || 0}`,
      change: {
        value: Math.abs(calculatePercentageChange(dashboardData.thisMonthEarnings, dashboardData.lastMonthEarnings))?.toFixed(2),
        type: dashboardData.thisMonthEarnings >= dashboardData.lastMonthEarnings ? "increase" : "decrease",
      },
      subtitle: `${dashboardData.thisMonthEarnings >= dashboardData.lastMonthEarnings ? "+" : ""}${formatPercentage(calculatePercentageChange(dashboardData.thisMonthEarnings, dashboardData.lastMonthEarnings))} from last month`,
    },
  ];

  const topContentColumns = [
    { title: "Content", dataIndex: "name", key: "name" },
    { title: "Views", dataIndex: "views", key: "views", render: (v) => v },
    { title: "Watch Time", dataIndex: "watchtime", key: "watchtime", render: (w) => formatWatchTime(w) },
    { title: "Engagement", dataIndex: "engagement", key: "engagement", render: (e) => `${e || 0}%` },
  ];

  return (
    <>
      <Head title="Creator Dashboard" />
      <div style={{ paddingBlock: "24px" }}>
        <div style={{ marginBottom: 24 }}>
          <DatePickerInput
            type="range"
            value={[dateRange[0].toDate(), dateRange[1].toDate()]}
            onChange={handleDateRangeChange}
            style={{ maxWidth: 400 }}
          />
        </div>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" mb="xl">
          {statsData.map((stat, index) => (
            <Paper withBorder p="md" key={index}>
              <Text size="xs" c="dimmed" mb={4}>{stat.title}</Text>
              <Text
                size="xl"
                fw={700}
                style={{ color: stat.change.type === "increase" ? "#4ade80" : "#f87171" }}
                mb={4}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {stat.change.type === "increase" ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                  {stat.value}
                </span>
              </Text>
              <Text size="xs" c="dimmed">{stat.subtitle}</Text>
            </Paper>
          ))}
        </SimpleGrid>

        <div style={{ marginBottom: 24 }}>
          <Title order={4} mb="md">Top Performing Content</Title>
          <DataTable
            dataSource={dashboardData.topPerformingContent}
            columns={topContentColumns}
            rowKey="_id"
            pagination={false}
          />
        </div>

        <SimpleGrid cols={{ base: 1, lg: 3 }} spacing="md">
          <Paper withBorder p="md" style={{ gridColumn: "span 2" }}>
            <Text fw={600} mb="md">Earnings Breakdown</Text>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
              <div>
                <Text size="xs" c="dimmed" mb={4}>Revenue</Text>
                <Text size="lg" fw={700}>Tsh {dashboardData.thisMonthRevenue.toFixed(2)}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" mb={4}>Subscription Revenue</Text>
                <Text size="lg" fw={700}>Tsh {dashboardData.thisMonthSubscriptionRevenue.toFixed(2)}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" mb={4}>Total Earnings</Text>
                <Text size="lg" fw={700}>Tsh {dashboardData.thisMonthEarnings.toFixed(2)}</Text>
              </div>
            </SimpleGrid>
          </Paper>

          <Paper withBorder p="md">
            <Text fw={600} mb="md">Performance Level</Text>
            <Text size="xs" c="dimmed" mb={4}>Current Level</Text>
            <Text size="lg" fw={700} mb="md">{dashboardData.levelStatus.currentLevel}</Text>
            <Text size="sm" c="dimmed">Next Milestone: {dashboardData.levelStatus.viewsLeft} Views</Text>
            <Text size="sm" c="dimmed" mt={4}>
              Progress: {((dashboardData.thisMonthViews / (dashboardData.levelStatus.viewsLeft + dashboardData.thisMonthViews)) * 100).toFixed(1)}%
            </Text>
          </Paper>
        </SimpleGrid>
      </div>
    </>
  );
};

export default CreatorDashboard;
